import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2, Waves, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem('lss_chat_session');
    if (!sid) {
      sid = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('lss_chat_session', sid);
    }
    return sid;
  } catch {
    return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

const DEFAULT_GREETING = "Hi! I'm Splash, Little Splash Swim's after-hours assistant. Ask me anything about our programs, pricing, schedule, or how to enroll — I'm happy to help.";

const NAME_KEY = 'lss_chat_name';

function getSavedName() {
  try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; }
}

function firstName(full) {
  return (full || '').trim().split(/\s+/)[0] || '';
}

function greetingFor(name) {
  const fn = firstName(name);
  if (!fn) return DEFAULT_GREETING;
  return `Welcome back, ${fn}! It's Splash — Little Splash Swim's assistant. What can I help you with today?`;
}

export default function ChatBubble() {
  const [savedName, setSavedName] = useState(getSavedName);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [{ role: 'assistant', text: greetingFor(getSavedName()) }]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoff, setHandoff] = useState({ name: '', email: '', question: '' });
  const [handoffSending, setHandoffSending] = useState(false);
  const [handoffSent, setHandoffSent] = useState(false);
  const [handoffError, setHandoffError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, streaming, handoffOpen, handoffSent]);

  // If a name gets saved after mount (from a handoff), refresh the greeting only if the chat is still on the default greeting.
  useEffect(() => {
    setMessages((m) => {
      if (m.length !== 1 || m[0].role !== 'assistant') return m;
      return [{ role: 'assistant', text: greetingFor(savedName) }];
    });
  }, [savedName]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error('Chat request failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const appendDelta = (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') copy[copy.length - 1] = { ...last, text: last.text + delta };
          return copy;
        });
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.delta) appendDelta(evt.delta);
            if (evt.error) appendDelta(`\n\n${evt.error}`);
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant' && !last.text) {
          copy[copy.length - 1] = { ...last, text: "Sorry, I couldn't reach the server. Please try again in a moment, or email info@littlesplashswimpro.com." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openHandoff = () => {
    // Prefill name from previous visits + question with the parent's last message
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    setHandoff((h) => ({
      ...h,
      name: h.name || savedName || '',
      question: h.question || lastUser?.text || '',
    }));
    setHandoffError('');
    setHandoffSent(false);
    setHandoffOpen(true);
  };

  const submitHandoff = async (e) => {
    e.preventDefault();
    if (!handoff.name.trim() || !handoff.email.trim() || !handoff.question.trim()) return;
    setHandoffSending(true);
    setHandoffError('');
    try {
      const resp = await fetch(`${API}/chat-handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          parent_name: handoff.name.trim(),
          parent_email: handoff.email.trim(),
          question: handoff.question.trim(),
        }),
      });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const data = await resp.json();
      if (!data.owner_notified) throw new Error(data.errors?.[0] || 'Failed to send');
      // Save name for return-visitor personalization
      try { localStorage.setItem(NAME_KEY, handoff.name.trim()); } catch { /* ignore */ }
      setSavedName(handoff.name.trim());
      setHandoffSent(true);
    } catch (err) {
      setHandoffError(err.message || 'Could not send. Please email info@littlesplashswimpro.com directly.');
    } finally {
      setHandoffSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-bubble-open"
          aria-label="Open Little Splash chat assistant"
          className="fixed bottom-20 right-4 sm:bottom-5 sm:right-5 z-40 flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white shadow-2xl p-3 sm:px-5 sm:py-3.5 font-semibold btn-transition"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline">Ask Little Splash</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-3 bottom-3 z-40 sm:inset-auto sm:bottom-5 sm:right-5 w-auto sm:w-[380px] max-h-[80vh] flex flex-col rounded-3xl bg-white shadow-2xl border border-splash-sand overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-splash-green text-splash-cream">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-splash-teal grid place-items-center shrink-0">
                <Waves className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-base leading-tight">Splash · AI Assistant</div>
                <div className="text-[11px] text-splash-cream/70">Answers 24/7 · Coach Abrie follows up</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              data-testid="chat-bubble-close"
              className="p-1.5 rounded-full hover:bg-white/10 btn-transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid="chat-scroll"
            className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-splash-cream min-h-[220px]"
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed px-3.5 py-2.5 rounded-2xl ${
                    m.role === 'user'
                      ? 'bg-splash-teal text-white rounded-br-md'
                      : 'bg-white text-splash-green border border-splash-sand rounded-bl-md'
                  }`}
                >
                  {m.text || (streaming && idx === messages.length - 1 ? (
                    <span className="inline-flex items-center gap-1 text-splash-green/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-splash-green/50 animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-splash-green/50 animate-pulse [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-splash-green/50 animate-pulse [animation-delay:300ms]" />
                    </span>
                  ) : '')}
                </div>
              </div>
            ))}

            {handoffOpen && !handoffSent && (
              <form
                onSubmit={submitHandoff}
                data-testid="chat-handoff-form"
                className="rounded-2xl bg-white border border-splash-sand p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">Email Coach Abrie</div>
                  <button
                    type="button"
                    onClick={() => setHandoffOpen(false)}
                    aria-label="Close handoff form"
                    className="p-1 rounded-full hover:bg-splash-cream text-splash-green/60"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  required
                  value={handoff.name}
                  onChange={(e) => setHandoff({ ...handoff, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="handoff-name"
                  className="w-full text-sm rounded-xl border border-splash-sand bg-splash-cream/60 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-splash-teal"
                />
                <input
                  required
                  type="email"
                  value={handoff.email}
                  onChange={(e) => setHandoff({ ...handoff, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="handoff-email"
                  className="w-full text-sm rounded-xl border border-splash-sand bg-splash-cream/60 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-splash-teal"
                />
                <textarea
                  required
                  rows={3}
                  value={handoff.question}
                  onChange={(e) => setHandoff({ ...handoff, question: e.target.value })}
                  placeholder="Your question for Coach Abrie…"
                  data-testid="handoff-question"
                  className="w-full text-sm rounded-xl border border-splash-sand bg-splash-cream/60 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-splash-teal resize-none"
                />
                {handoffError && (
                  <div className="text-xs text-splash-coralDark">{handoffError}</div>
                )}
                <button
                  type="submit"
                  disabled={handoffSending || !handoff.name.trim() || !handoff.email.trim() || !handoff.question.trim()}
                  data-testid="handoff-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-60 text-white text-sm px-4 py-2 font-semibold btn-transition"
                >
                  {handoffSending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  ) : (
                    <><Mail className="w-4 h-4" /> Send to Coach Abrie</>
                  )}
                </button>
                <div className="text-[11px] text-splash-green/60 leading-snug">
                  Your recent chat with Splash will be included so Coach Abrie has full context.
                </div>
              </form>
            )}

            {handoffSent && (
              <div
                data-testid="chat-handoff-success"
                className="rounded-2xl bg-splash-teal/10 border border-splash-teal/30 p-3 flex items-start gap-2 text-splash-green"
              >
                <CheckCircle2 className="w-5 h-5 text-splash-teal shrink-0 mt-0.5" />
                <div className="text-sm">
                  Your question is on its way to Coach Abrie. She'll reply within 24 hours.
                </div>
              </div>
            )}
          </div>

          {!handoffOpen && !handoffSent && (
            <button
              type="button"
              onClick={openHandoff}
              data-testid="chat-open-handoff"
              className="border-t border-splash-sand bg-white text-splash-green/80 hover:text-splash-green text-xs font-semibold py-2 flex items-center justify-center gap-1.5 btn-transition"
            >
              <Mail className="w-3.5 h-3.5" /> Prefer a human? Email Coach Abrie
            </button>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-end gap-2 border-t border-splash-sand bg-white px-3 py-3"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={streaming}
              rows={1}
              placeholder="Ask about lessons, pricing, schedule…"
              data-testid="chat-input"
              className="flex-1 resize-none max-h-32 rounded-2xl border border-splash-sand bg-splash-cream/60 px-3 py-2 text-sm text-splash-green focus:outline-none focus:ring-2 focus:ring-splash-teal disabled:opacity-70"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              aria-label="Send message"
              className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-50 disabled:cursor-not-allowed text-white btn-transition"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
